import type { IconName } from "@blueprintjs/core";
import type { ActionPayload } from "@package/ui-gql/index-data-access";
import type { SchedulerEventTone } from "./calendar-utils";
import type { SchedulerEntityNames } from "./use-scheduler-entity-names";

export type SchedulerActionDescription = {
  icon: IconName;
  tone: SchedulerEventTone;
  sentence: string;
};

/**
 * Turns an ActionPayload (which only carries related ids) into the
 * plain-language sentence + tone the preview card and calendar chips share,
 * resolving ids to labels through the lookups from
 * use-scheduler-entity-names.
 *
 * Discriminates on `__typename` rather than `type` -- codegen types the
 * `type` field as the general ActionPayloadType enum on every union member,
 * not a per-member literal, so it can't narrow the union. `__typename` is
 * the only field TypeScript can actually discriminate on here (same
 * workaround SchedulerEditActionEnablePost/ChangeLocationProfile already
 * use).
 */
export function describeSchedulerAction(
  action: ActionPayload | null | undefined,
  names: SchedulerEntityNames,
): SchedulerActionDescription | null {
  if (!action) {
    return null;
  }

  switch (action.__typename) {
    case "ActionPayloadLocationProfileChanged": {
      const location =
        names.locations.get(action.location) ?? "an unknown location";
      const profile = names.profiles.get(action.profile) ?? "an unknown canvas";

      return {
        icon: "desktop",
        tone: "profile",
        sentence: `${location} switches to ${profile}`,
      };
    }
    case "ActionPayloadPostEnabled": {
      const post = names.posts.get(action.post) ?? "an unknown post";

      return {
        icon: "media",
        tone: action.enabled ? "post-enable" : "post-disable",
        sentence: action.enabled ? `Enable "${post}"` : `Disable "${post}"`,
      };
    }
    default: {
      return null;
    }
  }
}
